/**
 * CardDescriptionSection — the CardDetailDrawer's Description block:
 * the dictation mic, the @mention-aware textarea, the Trello-sync notes,
 * and the typing-assist + AI actions bars.
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 */
import type { KanbanApi, KanbanMember } from '../../../api'
import type { Card } from '../../../types'
import { SpeechMicButton } from '../../SpeechMicButton'
import { MentionTextarea } from '../../MentionTextarea'
import { DescriptionActionsBar } from '../DescriptionActionsBar'
import { AiActionsBar } from '../AiActionsBar'
import { LiveSuggestionStrip } from '../LiveSuggestionStrip'
import type { DrawerData, DrawerEditing } from '../drawerHookTypes'
import { operatorVisibleSummary } from '../operatorVisibleSummary'

export interface CardDescriptionSectionProps {
  card: Card
  api: KanbanApi
  members?: KanbanMember[]
  boardId?: string
  editing: DrawerEditing
  data: DrawerData
}

export function CardDescriptionSection({
  card,
  api,
  members,
  boardId,
  editing,
  data,
}: CardDescriptionSectionProps) {
  const { summary, setSummary, patchCardSummary, dirtyFieldsRef, busyKey, setActionDate } = editing
  const { qc, pushFocusedCardUpdate } = data

  return (
    <section data-kanban-focus="description">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
          Description
        </h3>
        {/* Speech-to-text dictation. Auto-saves on each final chunk so a
            dictation-then-walk-away workflow doesn't lose the transcript. */}
        <SpeechMicButton
          size="sm"
          title="Dictate description"
          transcribe={api.transcribeAudio}
          onAppendText={(chunk, isFinal) => {
            if (!isFinal) return
            setSummary((current) => {
              const next = current
                ? `${current.replace(/\s+$/, '')} ${chunk.trim()} `
                : `${chunk.trim()} `
              // Auto-save each final segment in the background. Routes through
              // patchCardSummary so Trello-mirrored cards push back to Trello.
              void patchCardSummary(next.trim())
              return next
            })
          }}
        />
      </div>
      {/* @mention-aware description. On save the tagged ids ride along as
          ``summary_mentions`` so the backend notifies anyone newly @-tagged. */}
      <MentionTextarea
        value={summary}
        onChange={(v) => {
          dirtyFieldsRef.current.add('summary')
          setSummary(v)
        }}
        members={members ?? []}
        onBlur={() => {
          // Compare against the visible baseline, not raw card.summary.
          // A hidden email dump must not PATCH empty on first blur.
          if (summary !== operatorVisibleSummary(card)) {
            void patchCardSummary(summary)
          }
        }}
        placeholder="Add a more detailed description…  @ to tag a teammate"
        rows={4}
        ariaLabel="Card description"
        className="w-full resize-y rounded-lg border border-neutral-200 dark:border-neutral-700/70 bg-neutral-50 dark:bg-neutral-900 px-3 py-2.5 text-sm leading-relaxed text-neutral-800 dark:text-neutral-100 shadow-sm transition-colors hover:border-neutral-300 dark:hover:border-neutral-600 focus:border-blue-500 focus:bg-white dark:focus:bg-neutral-950 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
      {busyKey === 'summary' && (
        <p className="mt-1 text-[10px] text-neutral-400">
          {card.provider === 'trello' ? 'Saving to Trello…' : 'Saving…'}
        </p>
      )}
      {card.provider === 'trello' && busyKey !== 'summary' && (
        <p className="mt-1 text-[10px] text-sky-600 dark:text-sky-400">
          Description syncs back to Trello.
        </p>
      )}
      {/* Typing-assist actions bar — detects @name / due-date words / "- step"
          lines and offers tap-to-apply chips. Renders nothing until detected. */}
      <DescriptionActionsBar
        text={summary}
        card={card}
        api={api}
        qc={qc}
        boardId={boardId}
        members={members}
        onChanged={pushFocusedCardUpdate}
        onDueApplied={(iso) => setActionDate(iso)}
      />
      {/* On-demand "Structure with AI": the local LLM reinterprets the note
          into a follow-up/task/step + person + date the operator confirms. */}
      <AiActionsBar
        text={summary}
        card={card}
        api={api}
        qc={qc}
        members={members}
        onChanged={pushFocusedCardUpdate}
        onDueApplied={(iso) => setActionDate(iso)}
      />
      {/* U23 — ambient tick/cross suggestions while typing (debounced;
          server enforces min-chars + cooldown). Hides itself when the
          host/backend hasn't wired suggestCardActions. */}
      <LiveSuggestionStrip
        text={summary}
        card={card}
        api={api}
        qc={qc}
        onChanged={pushFocusedCardUpdate}
        onDueApplied={(iso) => setActionDate(iso)}
      />
    </section>
  )
}
